import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useAppSelector } from "../../app";
import { notification } from "antd";
import { useTranslation } from "react-i18next";
import usePasswordValidation from "./usePasswordValidation";
import { AxiosError } from "axios";
import { useUpdatePasswordMutation } from "../store/userApi";

type PasswordState = {
  newPassword: string;
  repeatPassword: string;
};

const defaultState = {
  newPassword: "",
  repeatPassword: "",
};

const usePassword = (disabled = false) => {
  const { user } = useAppSelector((state) => state.userReducer);

  const { t } = useTranslation("personalPage", {
    keyPrefix: "password",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [state, setState] = useState<PasswordState>(defaultState);
  const [validation, setValidation] = useState<string | undefined>("error");

  const { isValidPassword } = usePasswordValidation(state.newPassword);

  const [updatePassword] = useUpdatePasswordMutation();

  // Not purely derived: the `!state.repeatPassword` early return makes
  // `validation` sticky — clearing the repeat field keeps the last computed
  // value ("error"/"notMatching"/undefined) rather than recomputing. Deriving
  // during render would change what the field status and the "notMatching"
  // label show once the repeat field is emptied.
  useEffect(() => {
    if (!state.repeatPassword) {
      return;
    }

    if (!isValidPassword) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- state is sticky, not derived; see above
      setValidation("error");
      return;
    }

    if (state.repeatPassword !== state.newPassword) {
      setValidation("notMatching");
      return;
    }

    setValidation(undefined);
  }, [isValidPassword, state]);

  const handleEditingClick = useCallback(() => {
    if (disabled) {
      return;
    }

    setIsEditing((prevState) => !prevState);
  }, [disabled]);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setState((oldState) => ({
      ...oldState,
      [event.target.name]: event.target.value,
    }));
  }, []);

  const handlePasswordUpdate = useCallback(async () => {
    if (!user) {
      return;
    }

    try {
      const body = {
        password: state.newPassword,
      };

      await updatePassword({ userId: user.id, data: body }).unwrap();
      setIsEditing(false);
      notification.success({
        title: t("success.title"),
        description: t("success.description"),
      });
    } catch (e) {
      const error = e as AxiosError;

      console.error(e);
      notification.error({
        title: t("error.title"),
        description: error?.message || t("error.description"),
      });
    }
  }, [user, state, t, updatePassword]);

  const handleCancelClick = useCallback(() => {
    setState(defaultState);
    setIsEditing(false);
  }, []);

  return {
    state,
    validation,
    isEditing,
    handleChange,
    handlePasswordUpdate,
    handleEditingClick,
    handleCancelClick,
  };
};

export default usePassword;
